import Dexie, { type Table } from "dexie";
import { EVENT_ID } from "./types";
import type {
  DeferredCredit,
  FeePayoutRecord,
  Session,
  SyncMutation,
  Ticket,
} from "./types";
import type { CashCheckpoint, CashCountRecord } from "./cash";
import { emptyCashRecord, normalizeCashRecord } from "./cash";

export type DeviceRole = "general" | "cash" | "fees" | "field_sales" | "checkin";

export type FieldMutation =
  | SyncMutation
  | {
      kind: "field_ticket_create";
      mutationId: string;
      eventId: string;
      ticketId: string;
      deviceId: string;
      deviceSeq?: number;
      ticket: Ticket;
      createdAt: string;
    }
  | {
      kind: "field_ticket_add_seat";
      mutationId: string;
      eventId: string;
      ticketId: string;
      seats: number;
      deviceId: string;
      createdAt: string;
    }
  | {
      kind: "ticket_ops";
      mutationId: string;
      eventId: string;
      ticketId: string;
      opsStatus: string;
      deviceId: string;
      createdAt: string;
    }
  | {
      kind: "fee_payout";
      mutationId: string;
      eventId: string;
      session: Session;
      performer: string;
      amountPaid: number;
      deviceId: string;
      createdAt: string;
    }
  | {
      kind: "cash_count";
      mutationId: string;
      eventId: string;
      checkpoint: CashCheckpoint;
      counts: import("./cash").CashCounts | Record<string, number>;
      balance: number;
      income: number;
      feeSnapshot: number;
      feeLocked: boolean;
      deviceId: string;
      createdAt: string;
    }
  | {
      kind: "settle_topup" | "settle_refund" | "coupon_issue";
      mutationId: string;
      eventId: string;
      ticketId: string;
      deviceId: string;
      createdAt: string;
      creditSourceId?: string;
      amount?: number;
    }
  | {
      kind: "redeem_deferred";
      mutationId: string;
      eventId: string;
      ticketId: string;
      creditSourceId: string;
      couponIssued?: boolean;
      deviceId: string;
      createdAt: string;
    };

export interface FieldMeta {
  id: string;
  lastSync: string;
  cursor: string;
  ssot: "sheet" | "db";
  deviceRole: DeviceRole;
  pendingCount: number;
}

class FieldDatabase extends Dexie {
  tickets!: Table<Ticket, string>;
  outbox!: Table<FieldMutation, string>;
  feePayouts!: Table<FeePayoutRecord & { key: string }, string>;
  cashCounts!: Table<CashCountRecord, CashCheckpoint>;
  meta!: Table<FieldMeta, string>;
  performerOptions!: Table<{ session: Session; performers: string[] }, Session>;
  deferredCredits!: Table<DeferredCredit, string>;

  constructor() {
    super("ii-field-ledger-v1");
    this.version(1).stores({
      tickets: "id, source, kind",
      outbox: "mutationId, kind, createdAt",
      feePayouts: "key, session, performer",
      cashCounts: "checkpoint",
      meta: "id",
      performerOptions: "session",
    });
    this.version(2).stores({
      tickets: "id, source, kind, settleStatus",
      outbox: "mutationId, kind, createdAt",
      feePayouts: "key, session, performer",
      cashCounts: "checkpoint",
      meta: "id",
      performerOptions: "session",
      deferredCredits: "id, phone, redeemed",
    });
  }
}

export const fieldDb = new FieldDatabase();

const META_ID = EVENT_ID;

export async function getFieldMeta(): Promise<FieldMeta> {
  const existing = await fieldDb.meta.get(META_ID);
  return (
    existing || {
      id: META_ID,
      lastSync: "",
      cursor: "",
      ssot: "sheet",
      deviceRole: "general",
      pendingCount: 0,
    }
  );
}

export async function setFieldMeta(patch: Partial<FieldMeta>): Promise<FieldMeta> {
  const current = await getFieldMeta();
  const next = { ...current, ...patch, id: META_ID };
  await fieldDb.meta.put(next);
  return next;
}

export async function loadLocalSnapshot(): Promise<{
  tickets: Ticket[];
  feePayouts: FeePayoutRecord[];
  cashCounts: Record<CashCheckpoint, CashCountRecord>;
  performerOptions: Record<Session, string[]>;
  deferredCredits: DeferredCredit[];
  outbox: FieldMutation[];
  meta: FieldMeta;
}> {
  const [tickets, fees, cashRows, optionRows, deferredCredits, outbox, meta] = await Promise.all([
    fieldDb.tickets.toArray(),
    fieldDb.feePayouts.toArray(),
    fieldDb.cashCounts.toArray(),
    fieldDb.performerOptions.toArray(),
    fieldDb.deferredCredits.toArray(),
    fieldDb.outbox.toArray(),
    getFieldMeta(),
  ]);

  const cashCounts: Record<CashCheckpoint, CashCountRecord> = {
    opening: emptyCashRecord("opening"),
    afternoon: emptyCashRecord("afternoon"),
    evening: emptyCashRecord("evening"),
  };
  for (const row of cashRows) {
    cashCounts[row.checkpoint] = normalizeCashRecord(row);
  }

  const performerOptions: Record<Session, string[]> = { afternoon: [], evening: [] };
  for (const row of optionRows) {
    performerOptions[row.session] = row.performers || [];
  }

  return {
    tickets,
    feePayouts: fees.map(({ key: _key, ...rest }) => rest),
    cashCounts,
    performerOptions,
    deferredCredits,
    outbox,
    meta,
  };
}

export async function replaceLocalSnapshot(input: {
  tickets: Ticket[];
  feePayouts?: FeePayoutRecord[];
  cashCounts?: CashCountRecord[];
  performerOptions?: Record<Session, string[]>;
  deferredCredits?: DeferredCredit[];
  cursor?: string;
  ssot?: "sheet" | "db";
}): Promise<void> {
  await fieldDb.transaction(
    "rw",
    [
      fieldDb.tickets,
      fieldDb.feePayouts,
      fieldDb.cashCounts,
      fieldDb.performerOptions,
      fieldDb.deferredCredits,
      fieldDb.meta,
      fieldDb.outbox,
    ],
    async () => {
      await fieldDb.tickets.clear();
      await fieldDb.tickets.bulkPut(input.tickets);
      if (input.feePayouts) {
        await fieldDb.feePayouts.clear();
        await fieldDb.feePayouts.bulkPut(
          input.feePayouts.map((item) => ({
            ...item,
            key: `${item.session}:${item.performer}`,
          })),
        );
      }
      if (input.cashCounts) {
        for (const row of input.cashCounts) {
          await fieldDb.cashCounts.put(normalizeCashRecord(row));
        }
      }
      if (input.performerOptions) {
        await fieldDb.performerOptions.put({
          session: "afternoon",
          performers: input.performerOptions.afternoon || [],
        });
        await fieldDb.performerOptions.put({
          session: "evening",
          performers: input.performerOptions.evening || [],
        });
      }
      if (input.deferredCredits) {
        await fieldDb.deferredCredits.clear();
        await fieldDb.deferredCredits.bulkPut(input.deferredCredits);
      }
      const meta = await getFieldMeta();
      await fieldDb.meta.put({
        ...meta,
        cursor: input.cursor || meta.cursor,
        lastSync: input.cursor || new Date().toISOString(),
        ssot: input.ssot || meta.ssot,
        pendingCount: await fieldDb.outbox.count(),
      });
    },
  );
}

export async function appendOutbox(mutation: FieldMutation): Promise<void> {
  await fieldDb.transaction("rw", fieldDb.outbox, fieldDb.meta, async () => {
    await fieldDb.outbox.put(mutation);
    const meta = await getFieldMeta();
    await fieldDb.meta.put({
      ...meta,
      pendingCount: await fieldDb.outbox.count(),
    });
  });
}

export async function removeOutbox(mutationIds: string[]): Promise<void> {
  await fieldDb.transaction("rw", fieldDb.outbox, fieldDb.meta, async () => {
    await fieldDb.outbox.bulkDelete(mutationIds);
    const meta = await getFieldMeta();
    await fieldDb.meta.put({
      ...meta,
      pendingCount: await fieldDb.outbox.count(),
    });
  });
}

export async function saveTicketsLocal(tickets: Ticket[]): Promise<void> {
  await fieldDb.tickets.clear();
  await fieldDb.tickets.bulkPut(tickets);
}

export async function saveFeeLocal(payout: FeePayoutRecord): Promise<void> {
  await fieldDb.feePayouts.put({
    ...payout,
    key: `${payout.session}:${payout.performer}`,
  });
}

export async function saveCashLocal(record: CashCountRecord): Promise<void> {
  await fieldDb.cashCounts.put(normalizeCashRecord(record));
}

export async function saveDeferredCreditsLocal(credits: DeferredCredit[]): Promise<void> {
  await fieldDb.transaction("rw", fieldDb.deferredCredits, async () => {
    await fieldDb.deferredCredits.clear();
    await fieldDb.deferredCredits.bulkPut(credits);
  });
}
